import { Platform } from '@angular/cdk/platform';
import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { MEDIA_BREAKPOINTS_PROVIDER, MediaBreakpointsProvider } from './media-breakpoints.provider';
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  fromEvent,
  map,
  Observable,
  shareReplay,
  startWith,
} from 'rxjs';

const RESIZE_DEBOUNCE_MS = 100;

export
@Injectable({ providedIn: 'root' })
class MediaObserverService {
  private readonly mediaBreakpoints = inject<MediaBreakpointsProvider>(MEDIA_BREAKPOINTS_PROVIDER);
  private readonly platformId = inject<Platform | null>(PLATFORM_ID, { optional: true });

  public readonly width$: Observable<number>;

  public readonly breakpoint$: Observable<string>;

  public readonly isMobile$: Observable<boolean>;

  constructor() {
    this.width$ = this.buildWidthObservable();
    this.breakpoint$ = this.buildBreakpointObservable();
    this.isMobile$ = this.buildIsMobileObservable();
  }

  public get isMobile(): boolean {
    const breakpoint = this.matchWidthWithBreakpoint(window.innerWidth);
    return breakpoint ? this.isBreakpointDown(breakpoint, 'md', false) : false;
  }

  public isBreakpointDown(current: string, target: string, includeEquals = true): boolean {
    return includeEquals
      ? this.mediaBreakpoints[current] <= this.mediaBreakpoints[target]
      : this.mediaBreakpoints[current] < this.mediaBreakpoints[target];
  }

  public isBreakpointUp(current: string, target: string, includeEquals = true): boolean {
    return includeEquals
      ? this.mediaBreakpoints[current] >= this.mediaBreakpoints[target]
      : this.mediaBreakpoints[current] > this.mediaBreakpoints[target];
  }

  public isBreakpointEqual(current: string, target: string): boolean {
    return this.mediaBreakpoints[current] === this.mediaBreakpoints[target];
  }

  protected buildWidthObservable(): Observable<number> {
    if (this.platformId && isPlatformBrowser(this.platformId)) {
      return fromEvent(window, 'resize').pipe(
        startWith(window),
        map(() => window as Window),
        map(w => w.innerWidth),
        shareReplay()
      );
    } else {
      return EMPTY;
    }
  }

  protected buildBreakpointObservable(): Observable<string> {
    return this.width$.pipe(
      debounceTime(RESIZE_DEBOUNCE_MS),
      map(width => this.matchWidthWithBreakpoint(width)),
      filter(Boolean),
      distinctUntilChanged(),
      shareReplay()
    );
  }

  protected matchWidthWithBreakpoint(width: number): string | null {
    let lastBreakpoint = Object.keys(this.mediaBreakpoints)[0];

    if (!lastBreakpoint) {
      return null;
    }

    for (const breakpoint in this.mediaBreakpoints) {
      const maxWidth = this.mediaBreakpoints[breakpoint];

      if (width > maxWidth) {
        lastBreakpoint = breakpoint;
      } else {
        return lastBreakpoint;
      }
    }

    return lastBreakpoint;
  }

  protected buildIsMobileObservable(): Observable<boolean> {
    return this.breakpoint$.pipe(
      map(breakpoint => this.isBreakpointDown(breakpoint, 'md', false)),
      distinctUntilChanged(),
      shareReplay()
    );
  }
}
